import React from 'react';
import styled, { css, keyframes } from 'styled-components/macro';

import Box from '../Box';

const SIZE = 44;

const circularRotate = keyframes`
  0% {
    transform-origin: 50% 50%;
  }
  100% {
    transform: rotate(360deg);
  }
`;

const getCircularDash = (sizeRate) => keyframes`
  0% {
    stroke-dasharray: ${sizeRate}px, ${sizeRate * 200}px;
    stroke-dashoffset: 0px;
  }
  50% {
    stroke-dasharray: ${sizeRate * 100}px, ${sizeRate * 200}px;
    stroke-dashoffset: ${sizeRate * -15}px;
  }
  100% {
    stroke-dasharray: ${sizeRate * 100}px, ${sizeRate * 200}px;
    stroke-dashoffset: ${sizeRate * -125}px;
  }
`;

const SpinnerWrapper = styled(Box)`
  display: inline-block;

  ${({ variant }) =>
    variant === 'indeterminate'
      ? css`
          animation: ${css`
            ${circularRotate} 1.4s linear infinite
          `};
        `
      : css`
          transition: transform 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
          transform: rotate(-90deg);
        `};

  ${({ size, thickness, color }) => css`
    width: ${size + thickness}px;
    height: ${size + thickness}px;
    color: ${color};
    margin: -${thickness / 2}px;
  `}

  .svg {
    display: block;
  }

  ${({ size }) => css`
    stroke: currentColor;
    .circleIndeterminate {
      animation: ${css`
        ${getCircularDash(size / SIZE)} 1.4s ease-in-out infinite
      `};
      stroke-dasharray: ${(size / SIZE) * 80}px, ${(size / SIZE) * 200}px;
      stroke-dashoffset: 0px;
    }
    .circleDeterminate {
      transition: stroke-dashoffset 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
    }
  `}
`;

const CircularProgress = React.forwardRef(
  function CircularProgress(props, ref) {
    const {
      color = '#333333',
      size = '45px',
      thickness = 2,
      variant = 'indeterminate',
      value = 0,
      ...other
    } = props;
    const sizeNum = parseFloat(size);
    const circleStyle = {};
    const rootProps = {};

    if (variant === 'determinate') {
      const circumference = Math.PI * sizeNum;

      circleStyle.strokeDasharray = circumference.toFixed(3);
      rootProps['aria-valuenow'] = Math.round(value);
      circleStyle.strokeDashoffset = `${(
        ((100 - value) / 100) *
        circumference
      ).toFixed(3)}px`;
    }

    return (
      <SpinnerWrapper
        ref={ref}
        size={sizeNum}
        thickness={thickness}
        color={color}
        role="progressbar"
        variant={variant}
        {...rootProps}
        {...other}
      >
        <svg
          className="svg"
          viewBox={`0 0 ${sizeNum + thickness} ${sizeNum + thickness}`}
        >
          <circle
            className={
              variant === 'indeterminate'
                ? 'circleIndeterminate'
                : 'circleDeterminate'
            }
            style={circleStyle}
            cx={(sizeNum + thickness) / 2}
            cy={(sizeNum + thickness) / 2}
            r={sizeNum / 2}
            fill="none"
            strokeWidth={thickness}
          />
        </svg>
      </SpinnerWrapper>
    );
  },
);

export default CircularProgress;
